import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { clientBookingService, BookingListItem } from '@/services/clientBookingService';
import { BookingCardSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { useScrollContainer } from '@/contexts/ScrollContainerContext';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace('IDR', 'Rp.');
};

const formatIndonesianDate = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const weekday = date.toLocaleDateString('id-ID', { weekday: 'long' });
  const day = date.getDate();
  const month = date.toLocaleDateString('id-ID', { month: 'short' });
  const year = date.getFullYear();
  return `${weekday}, ${day} ${month} ${year}`;
};

// Tab Button component defined outside to prevent unmounting re-renders
const TabButton = ({
  tab,
  statusFilter,
  icon,
  label,
  onClick,
}: {
  tab: string;
  statusFilter: string;
  icon: string;
  label: string;
  onClick: (tab: string) => void;
}) => (
  <button
    type="button"
    onClick={() => onClick(tab)}
    className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-all duration-300 ease-in-out cursor-pointer ${
      statusFilter === tab
        ? 'border-sporto-orange text-sporto-orange'
        : 'border-transparent text-sporto-subtext hover:text-sporto-dark'
    }`}
  >
    <Icon icon={icon} className="w-4 h-4" />
    <span>{label}</span>
  </button>
);

const ClientBookingList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('upcoming');

  const limit = 10;
  const scrollRef = useScrollContainer();

  const fetchBookings = useCallback(async (page: number, status: string, silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const response = await clientBookingService.getClientBookings(page, limit, status);

      if (response.success) {
        setBookings(response.data.bookings || []);
        setCurrentPage(response.data.pagination?.current_page || 1);
        setTotalPages(response.data.pagination?.total_pages || 1);
        setTotal(response.data.pagination?.total || 0);
      } else {
        setError(response.message || 'Gagal memuat daftar booking');
        setBookings([]);
        setTotal(0);
      }
    } catch (err: unknown) {
      const errMsg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(errMsg || 'Gagal memuat daftar booking');
      setBookings([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  const { refreshing } = usePullToRefresh({
    onRefresh: () => fetchBookings(currentPage, statusFilter, true),
    enabled: true,
    scrollRef: scrollRef ?? undefined,
  });

  useEffect(() => {
    void fetchBookings(currentPage, statusFilter);
  }, [currentPage, statusFilter, fetchBookings]);

  const handleStatusChange = (status: string) => {
    if (status === statusFilter) return;
    setBookings([]);
    setTotal(0);
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const openDetail = (invoiceNumber: string) => {
    navigate(`/booking/${invoiceNumber}`);
  };

  return (
    <div className="bg-sporto-bg text-sporto-dark min-h-screen pb-12">
      {/* Mobile Sticky Tabs (Flush with Top Header - matching ClientMabarPage) */}
      <div className="sticky top-0 z-20 bg-white border-b border-sporto-border shadow-xs md:hidden">
        <div className="flex items-center border-t border-sporto-border/60 px-2">
          <TabButton tab="upcoming" statusFilter={statusFilter} onClick={handleStatusChange} icon="ph:calendar-check" label="Aktif" />
          <TabButton tab="ended" statusFilter={statusFilter} onClick={handleStatusChange} icon="ph:clock-counter-clockwise" label="Riwayat" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-4 md:pt-6 space-y-5">
        {refreshing && (
          <div className="flex justify-center md:hidden">
            <Icon icon="mdi:loading" className="w-6 h-6 animate-spin text-[#FC480D]" />
          </div>
        )}
        {/* Desktop Header (Hidden on Mobile) */}
        <div className="hidden md:flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-sporto-dark">Booking Saya</h1>
            <p className="text-xs text-sporto-subtext mt-0.5">Kelola jadwal dan riwayat reservasi lapanganmu</p>
          </div>
          <span className="text-xs text-sporto-subtext font-medium">Total: {total} booking</span>
        </div>

        {/* Desktop Tab Navigation (Card Container Style - matching ClientMabarPage) */}
        <div className="hidden md:flex items-center border-b border-[#E0E6ED] bg-white rounded-2xl px-6 mb-6 shadow-[inset_0_0_0_1px_#E0E6ED]">
          <TabButton tab="upcoming" statusFilter={statusFilter} onClick={handleStatusChange} icon="ph:calendar-check" label="Aktif" />
          <TabButton tab="ended" statusFilter={statusFilter} onClick={handleStatusChange} icon="ph:clock-counter-clockwise" label="Riwayat" />
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <BookingCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="bg-white rounded-3xl p-8 text-center border border-[#E0E6ED] shadow-2xs">
            <Icon icon="ph:warning-circle-bold" className="w-10 h-10 text-red-500 mx-auto mb-2" />
            <p className="text-sm font-bold text-[#0E1726] mb-1">Gagal Memuat Booking</p>
            <p className="text-xs text-[#556575]">{error}</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-3xl p-10 text-center border border-[#E0E6ED] shadow-2xs">
            <Icon icon="ph:calendar-blank-bold" className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-bold text-[#0E1726] mb-1">Belum Ada Booking</p>
            <p className="text-xs text-[#556575]">
              {statusFilter === 'upcoming'
                ? 'Kamu belum memiliki booking aktif saat ini.'
                : 'Belum ada riwayat booking yang selesai.'}
            </p>
          </div>
        ) : (
          <div className="animate-in fade-in duration-150 space-y-4">
            {bookings.map((booking, index) => {
              const isUnpaid =
                booking.payment_status?.toLowerCase() === 'unpaid' ||
                booking.payment_status?.toLowerCase() === 'pending';
              const isEnded = booking.booking_status?.toLowerCase() === 'ended' || statusFilter === 'ended';

              return (
                <div
                  key={`${booking.invoice_number}-${booking.booking_date}-${booking.start_time}-${index}`}
                  className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-3.5 hover:shadow-xs transition-all"
                >
                  {/* Top Row: Sport Category & Status Badges */}
                  <div className="flex items-center justify-between gap-2">
                    {/* Sport Category Badge */}
                    <div className="bg-[#FFF2EE] text-[#FC480D] text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                      <Icon icon="ph:soccer-ball" className="w-3.5 h-3.5" />
                      <span>{booking.sport_name || 'Futsal'}</span>
                    </div>

                    {/* Status Badge */}
                    {isUnpaid ? (
                      <div className="bg-amber-50 text-amber-600 text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                        <Icon icon="ph:credit-card" className="w-3.5 h-3.5" />
                        <span>Belum Bayar</span>
                      </div>
                    ) : isEnded ? (
                      <div className="bg-gray-100 text-gray-500 text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                        <Icon icon="ph:clock-counter-clockwise" className="w-3.5 h-3.5" />
                        <span>Selesai</span>
                      </div>
                    ) : (
                      <div className="bg-[#E6F8ED] text-[#0BCF46] text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                        <Icon icon="ph:check-circle" className="w-3.5 h-3.5 text-[#0BCF46]" />
                        <span>Terkonfirmasi</span>
                      </div>
                    )}
                  </div>

                  {/* Title: Venue Name */}
                  <h3 className="text-base md:text-lg font-bold text-[#0E1726]">
                    {booking.arena_name}
                  </h3>

                  {/* Information List (Date, Time, Field) */}
                  <div className="space-y-1.5 text-xs font-medium text-[#556575]">
                    <div className="flex items-center gap-2">
                      <Icon icon="ph:calendar-blank" className="w-4 h-4 text-gray-400 shrink-0" />
                      <span>{formatIndonesianDate(booking.booking_date)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Icon icon="ph:clock" className="w-4 h-4 text-gray-400 shrink-0" />
                      <span>{booking.start_time} - {booking.end_time}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Icon icon="ph:dribbble" className="w-4 h-4 text-gray-400 shrink-0" />
                      <span>
                        {booking.field_name ||
                          (booking.field_count > 1 ? `${booking.field_count} Lapangan` : 'Lapangan A')}
                      </span>
                    </div>
                  </div>

                  {/* Divider Line & Footer */}
                  <div className="border-t border-[#E0E6ED] pt-3 flex items-center justify-between gap-3">
                    <div>
                      <span className="text-xs text-gray-400 font-medium block mb-0.5">Total</span>
                      <span className="text-base md:text-lg font-bold text-[#FC480D]">
                        {formatCurrency(booking.total_price)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {isUnpaid && (
                        <button
                          type="button"
                          onClick={() => navigate(`/event/checkout/${booking.invoice_number}`)}
                          className="h-9 px-4 rounded-full bg-[#FC480D] hover:bg-[#D83B08] active:scale-95 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                        >
                          <Icon icon="ph:credit-card-bold" className="w-3.5 h-3.5 text-white" />
                          <span>Bayar Sekarang</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => openDetail(booking.invoice_number)}
                        className="h-9 px-4 rounded-full border border-[#E0E6ED] bg-white text-[#0E1726] hover:bg-gray-50 text-xs font-semibold flex items-center justify-center gap-1 transition-all cursor-pointer shadow-2xs"
                      >
                        <span>Detail</span>
                        <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-[#E0E6ED]">
            <span className="text-xs text-gray-500">
              Halaman {currentPage} dari {totalPages}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientBookingList;
